import {
	InventoryItemAllocationBoolean,
	InventoryItemAllocationQuantity,
} from 'common/modules/inventory/types';
import { isInventoryAllocationOption } from 'common/modules/inventory/utils';
import {
	InventoryItem,
	SkuItem,
	InventoryField,
	BaseInventoryItem,
	SingleInventoryItem,
	BulkInventoryItem,
	isSingleItem,
	isInventoryItemAllocationBoolean,
	isInventoryItemAllocationQuantity,
} from 'common/modules/inventory';
import { getTypedKeys } from 'common/utils/objects';
import { isEmpty, isEqual, merge, sum } from 'lodash';
import { getNewInventoryItem } from '../../utils';
import { DeepPartial, Location, PurchaseTypes } from 'common/types';
import { newFirestoreId } from 'common/utils/newRentalUtils';
import { stockItemToRow } from '../../ArticlesView/ArticlesTable/rows';
import { hasSameItems, sumItems } from 'common/utils/arrays';
import { switchUnreachable, removeUndefinedValues } from 'common/utils/common';
import {
	AllowedImportField,
	articleCodesArrayFromString,
	articleCodesStringFromArray,
	DataRowObject,
	getLocationIdByRowLocationName,
	getValidValuesText,
	inventoryStatusList,
	sanitizeDataRowObject,
	SanitizedDataRowObject,
} from '../utils';
import {
	FixedArticleColumnField,
	FixedArticleColumns,
} from 'views/Inventory/ArticlesView/ArticlesTable/columns';
import { TFunction } from 'i18next';
import errorHandler from 'common/services/errorHandler';

const {
	id,
	article_code,
	sku_code,
	sku_name,
	store,
	status,
	allocations,
	type,
	quantity,
	sole_length,
	din_variation,
} = FixedArticleColumns;
export interface ExistingInventoryData {
	articleCodes: string[];
	locationIdsByBulkSkuCode: {
		[skuCode: string]: string[];
	};
}
interface BaseArticleImportReq {
	shopId: string;
	userLocations: Location[];
	skuItems: SkuItem[];
	inventoryFields: InventoryField[];
	isMultiStore: boolean;
	existingInventoryData: ExistingInventoryData;
	t: TFunction;
}

type ArticleImportUpdateObject = Pick<InventoryItem, 'id'> & DeepPartial<InventoryItem>;

export type ArticleImport = {
	toAdd: {
		data: InventoryItem;
		csvFields: {
			data: SanitizedDataRowObject;
		};
	}[];
	toUpdate: {
		data: ArticleImportUpdateObject;
		existingData: InventoryItem;
		csvFields: {
			data: SanitizedDataRowObject;
			updatedFields: (keyof SanitizedDataRowObject)[];
		};
	}[];
	invalid: {
		csvFields: {
			data: SanitizedDataRowObject;
			invalidFields: { [key in keyof SanitizedDataRowObject]: string };
		};
	}[];
};

export interface GetArticlesImportData extends BaseArticleImportReq {
	rows: DataRowObject[];
	existingArticles: InventoryItem[];
}

export const getExistingArticle = (
	row: SanitizedDataRowObject,
	existingArticles: InventoryItem[],
	opts: { userLocations: Location[] },
) => {
	if (row.id != null) {
		return existingArticles.find((a) => a.id === row.id);
	}
	const isBulkArticleRow = row.type?.toLocaleLowerCase() === 'bulk';
	const isSingleArticleRow = row.type?.toLocaleLowerCase() === 'single';
	const rowArticleCodes: string[] | undefined = row.article_code
		? articleCodesArrayFromString(row.article_code)
		: undefined;
	if (rowArticleCodes?.length && (!!isSingleArticleRow || !isBulkArticleRow)) {
		return existingArticles.find((article) =>
			rowArticleCodes.some((code) => article.identifiers.includes(code)),
		);
	}
	if (!isSingleArticleRow) {
		const locationId = getLocationIdByRowLocationName(row.store ?? null, opts.userLocations);
		const skuCode = row.sku_code;
		if (!skuCode || !locationId) return undefined;
		return existingArticles.find(
			(article) =>
				article.type === 'bulk' && article.skuCode === skuCode && article.locationId === locationId,
		);
	}
	return undefined;
};

export const getArticlesImportData = (args: GetArticlesImportData): ArticleImport => {
	const { rows, existingArticles, inventoryFields, userLocations } = args;
	let processedArticles: InventoryItem[] = [];
	const articles = rows.reduce(
		(tot, _row) => {
			const row = sanitizeDataRowObject(
				_row,
				inventoryFields.map((f) => f.id),
			);
			const existingArticle = getExistingArticle(row, existingArticles, { userLocations });
			const articleData = getSingleArticleImportData({
				...args,
				row,
				existingArticle,
				processedArticles: processedArticles,
			});
			if (articleData.type === 'equal_to_existing') {
				processedArticles.push(articleData.data);
				return tot;
			}
			if (articleData.type === 'invalid') {
				return {
					...tot,
					invalid: [...tot.invalid, articleData.data],
				};
			}
			if (articleData.type === 'update') {
				processedArticles.push(merge({}, articleData.data.existingData, articleData.data.data));
				return {
					...tot,
					toUpdate: [...tot.toUpdate, articleData.data],
				};
			}
			processedArticles.push(articleData.data.data);
			return {
				...tot,
				toAdd: [...tot.toAdd, articleData.data],
			};
		},
		{ toAdd: [], toUpdate: [], invalid: [] } as ArticleImport,
	);
	return articles;
};

export const articleStatusesFromString = (
	statusesString: string | undefined,
): SingleInventoryItem['status'] | BulkInventoryItem['statuses'] | undefined => {
	if (inventoryStatusList.includes(statusesString as any)) {
		return statusesString as SingleInventoryItem['status'];
	}
	if (!statusesString) return undefined;
	let hasInvalidStatusFormat = false;
	const statuses = statusesString.toLocaleUpperCase().split(',');
	const statusesObject = statuses.reduce((tot, statusString) => {
		const [statusName, statusValue] = statusString.trim().split(':');
		const value =
			statusValue != null && !isNaN(Number(statusValue)) ? Number(statusValue) : undefined;
		const validStatus = inventoryStatusList.includes(statusName as any);
		if (!validStatus || value === undefined) {
			hasInvalidStatusFormat = true;
			return tot;
		}
		return {
			...tot,
			[statusName]: value,
		};
	}, {} as BulkInventoryItem['statuses']);
	if (isEmpty(statusesObject) || hasInvalidStatusFormat) return undefined;
	return statusesObject;
};

export const articleAllocationFromString = (
	allocationsString: string | undefined,
): InventoryItemAllocationBoolean | InventoryItemAllocationQuantity => {
	if (!allocationsString) return {};
	switch (allocationsString) {
		case PurchaseTypes.rental: {
			return {
				rental: true,
				sales: false,
			};
		}
		case PurchaseTypes.sales: {
			return {
				sales: true,
				rental: false,
			};
		}
		default: {
			const allocationStrings = allocationsString.split(',');
			return allocationStrings.reduce((result, item) => {
				const [type, amount = 0] = item.split(':');
				if (isInventoryAllocationOption(type)) {
					result[type] = Number(amount);
				}
				return result;
			}, {} as InventoryItemAllocationQuantity);
		}
	}
};

const getImportedArticleRowFieldDiff = (
	row: SanitizedDataRowObject,
	existingArticle: InventoryItem,
	storeLocations: Location[],
) => {
	const existingArticleAsDataRow = stockItemToRow(existingArticle, storeLocations);
	const fieldDiffs = getTypedKeys(row).filter((key) => {
		const fieldKey = key as AllowedImportField | (string & { _?: never });
		switch (fieldKey) {
			case id:
				// We never update the ID based on the cell value, so it should be ignored in diff check
				return false;
			case article_code: {
				const newValue = row.article_code ? articleCodesArrayFromString(row.article_code) : [];
				const existingValue = existingArticleAsDataRow.article_code;
				return !hasSameItems(newValue, existingValue);
			}
			case status:
				const existingValue = existingArticleAsDataRow.status;
				const newValue = row.status;
				const existingFieldNotFoundAndNewValueEmpty =
					existingValue == null && (newValue == null || newValue === '');
				if (newValue == null || existingFieldNotFoundAndNewValueEmpty) return false;
				return !isEqual(existingValue, articleStatusesFromString(newValue));
			case allocations: {
				const existingValue = existingArticleAsDataRow.allocations;
				const newValue = row.allocations;
				return !isEqual(existingValue, articleAllocationFromString(newValue));
			}
			default: {
				const existingValue = existingArticleAsDataRow[key];
				const existingIsNumber = typeof existingValue === 'number' ? true : false;
				const newValue = row[key];
				const existingFieldNotFoundAndNewValueEmpty =
					existingValue == null && (newValue == null || newValue === '');
				if (newValue == null || existingFieldNotFoundAndNewValueEmpty) return false;
				return existingValue !== (existingIsNumber ? Number(newValue) : newValue);
			}
		}
	});
	return fieldDiffs;
};

type ArticleValidationResult =
	| { data: null; errors: { [key in keyof SanitizedDataRowObject]: string } }
	| {
			errors: null;
			data: {
				articleIds: string[] | undefined;
				skuItem: SkuItem | undefined;
				status: SingleInventoryItem['status'] | BulkInventoryItem['statuses'] | undefined; // TODO
				allocations:
					| SingleInventoryItem['allocation']
					| BulkInventoryItem['allocations']
					| undefined;
				type: InventoryItem['type'] | undefined;
				quantity: number | undefined;
				locationId: string | undefined;
				soleLength: string | undefined;
				dinVariation: string | undefined;
			};
	  };

export const getRequiredNewArticleFields = (
	row: SanitizedDataRowObject,
	opts: { isMultiStore: boolean },
) => {
	let requiredFields: AllowedImportField[] = [sku_code, sku_name, status, type, quantity];
	if (row[type]?.toLocaleLowerCase() !== 'bulk') {
		requiredFields.push(article_code);
	}
	if (opts.isMultiStore) {
		requiredFields.push(store);
	}
	return requiredFields;
};

const getNewArticleMissingRequiredFields = (
	row: SanitizedDataRowObject,
	opts: {
		isMultiStore: boolean;
		userLocations: Location[];
	},
	t: TFunction,
) => {
	const { isMultiStore, userLocations } = opts;
	const fieldErrors: { [key in keyof SanitizedDataRowObject]: string } = {};
	const requiredFields = getRequiredNewArticleFields(row, { isMultiStore });
	const fieldCannotBeEmptyString = t('validation.fieldCannotBeEmpty', 'This field cannot be empty');
	const emptyOrNotDefinedFields = requiredFields
		.filter((field) => row[field] === '' || row[field] == null)
		// If type is single, and quantity field is missing, it's ok since quantity is always 1
		.filter((field) => field !== quantity || (field === quantity && row[type] !== 'single'));
	emptyOrNotDefinedFields.forEach((field) => {
		const validValuesText =
			field === 'store'
				? getValidValuesText(t, 'store', userLocations)
				: getValidValuesText(t, field);
		fieldErrors[field] = `${fieldCannotBeEmptyString}. ${validValuesText}`;
	});
	return fieldErrors;
};

const validateArticleFields = (
	row: SanitizedDataRowObject,
	opts: {
		existingArticle: InventoryItem | undefined;
		skuItems: SkuItem[];
		userLocations: Location[];
		isMultiStore: boolean;
		existingInventoryData: ExistingInventoryData;
		processedArticles: ArticleImportUpdateObject[];
	},
	t: TFunction,
): ArticleValidationResult => {
	const {
		existingArticle,
		skuItems,
		userLocations,
		isMultiStore,
		existingInventoryData,
		processedArticles,
	} = opts;
	// When showing the import results, we want to always have an article_code and sku_code fields visible event if they are not added in the CSV
	if (row.article_code == null) {
		row.article_code = !!existingArticle
			? articleCodesStringFromArray(existingArticle?.identifiers)
			: '';
	}
	if (row.sku_code == null && !!existingArticle?.skuCode) {
		row.sku_code = existingArticle.skuCode;
	}
	const existingItemType = existingArticle?.type;
	const rowArticleCodes: string[] | undefined = row.article_code
		? articleCodesArrayFromString(row.article_code)
		: undefined;
	const skuCode: string | undefined = row.sku_code;
	const rowStatus: string | undefined = row.status;
	const rowAllocations: string | undefined = row.allocations;
	const rowType: string | undefined = row.type;
	const rowQuantity: string | undefined = row.quantity;
	const rowStore: string | undefined = row.store;
	const soleLength = row.sole_length;
	const dinVariation = row.din_variation;

	const skuItem = skuItems.find((item) => item.skuCode === skuCode);
	const skuItemType = skuItem?.itemType;
	const itemLocationId = getLocationIdByRowLocationName(rowStore ?? null, userLocations);
	const importItemType: InventoryItem['type'] | undefined =
		rowType === 'bulk' ? 'bulk' : rowType === 'single' ? 'single' : undefined;
	const articleItemType = existingItemType ?? importItemType;
	const itemTypeChanged =
		!!importItemType && !!existingItemType && existingItemType !== importItemType;
	const importItemStatus = rowStatus ? articleStatusesFromString(rowStatus) : undefined;
	const importItemAllocations = !!rowAllocations
		? articleAllocationFromString(rowAllocations)
		: undefined;
	const importItemStatusQuantity =
		typeof importItemStatus === 'object'
			? sumItems(Object.values(importItemStatus).map((val) => Number(val) || 0))
			: undefined;
	const importItemQuantity = !!rowQuantity ? Number(rowQuantity) : undefined;
	const quantityChange = (existingArticle as BulkInventoryItem)?.quantity !== importItemQuantity;
	const statusChange = !existingArticle
		? true
		: !isEqual(
				importItemStatus,
				isSingleItem(existingArticle) ? existingArticle.status : existingArticle.statuses,
		  );
	let fieldErrors: { [key in keyof DataRowObject]: string } = {};

	const getInvalidFieldValue = (key: AllowedImportField, row: SanitizedDataRowObject) => {
		if (typeof row[key] !== 'string') {
			return t('validation.fieldMustBeText', 'Field must be a text value');
		}
		switch (key) {
			case id: {
				break;
			}
			case article_code: {
				const noAccessToEditArticle =
					!!existingArticle && !userLocations.find((l) => l.id === existingArticle.locationId);
				const newArticleCodes = rowArticleCodes?.filter(
					(code) => !existingArticle?.identifiers.includes(code),
				);
				const codeExistsInInventory = newArticleCodes?.some((code) =>
					existingInventoryData.articleCodes.includes(code),
				);
				const bulkArticleForSkuAndLocationExistsInInventory =
					!existingArticle && row.sku_code && itemLocationId
						? existingInventoryData.locationIdsByBulkSkuCode[row.sku_code]?.includes(itemLocationId)
						: false;
				const alreadyUpdatedArticleCodes = processedArticles.flatMap((a) => a.identifiers);
				const codeAlreadyAddedInOtherRow = rowArticleCodes?.some((code) =>
					alreadyUpdatedArticleCodes.includes(code),
				);
				const alreadyUpdatedBulkArticles = processedArticles.filter((a) => a.type === 'bulk');
				const bulkTypeWithSameSkuAndLocationId = alreadyUpdatedBulkArticles.some(
					(a) => a.skuCode === skuItem?.skuCode && a.locationId === itemLocationId,
				);
				if (noAccessToEditArticle) {
					return t(
						'inventory.import.cannotEditArticle',
						'You cannot edit this article. The article belongs to a store that you do not have access.',
					);
				}
				if (!rowArticleCodes?.length && articleItemType === 'single') {
					return t('validation.fieldCannotBeEmpty');
				}
				if (codeExistsInInventory) {
					return t(
						'inventory.import.articleWithCodeAlreadyExists',
						'Article with this code already exists in the inventory',
					);
				}
				if (bulkArticleForSkuAndLocationExistsInInventory) {
					return t(
						'inventory.import.bulkArticleAlreadyExists',
						'A bulk article with this SKU and store already exists in the inventory',
					);
				}
				if (codeAlreadyAddedInOtherRow) {
					return t(
						'inventory.import.articleAlreadyImported',
						'This code is already found in another import row',
					);
				}
				if (bulkTypeWithSameSkuAndLocationId) {
					return t(
						'inventory.import.bulkArticleAlreadyImported',
						'A bulk article with the same SKU and store is already found in another import row',
					);
				}
				break;
			}
			case sku_code: {
				if (!!row[sku_code] && !skuItem) {
					return t('inventory.import.errorFindingSku', {
						defaultValue: 'Error finding a SKU for code {{code}}',
						code: row[sku_code],
					});
				}
				break;
			}
			case sku_name: {
				if (!row[sku_code] && !!row[sku_name]) {
					return t('inventory.import.skuNameAndCodeRequired', {
						defaultValue: 'If {{col1}} column is present, also {{col2}} column is required',
						col1: sku_name,
						col2: sku_code,
					});
				}
				break;
			}
			case store: {
				const validValuesText = getValidValuesText(t, 'store', userLocations);
				if (!rowStore && !itemLocationId) {
					return `${t('validation.fieldCannotBeEmpty')}. ${validValuesText}`;
				}
				if (!!rowStore && !itemLocationId) {
					return `${t('validation.incorrectValue')} ${row[store]}. ${validValuesText}`;
				}
				break;
			}
			case sole_length:
			case din_variation: {
				if (isNaN(Number(row[key]))) {
					return t('validation.fieldMustBeNumber', 'This field must be a number');
				}
				break;
			}
			case quantity: {
				if (importItemQuantity != null && isNaN(Number(importItemQuantity))) {
					return t('validation.fieldMustBeNumber', 'This field must be a number');
				}
				if (
					articleItemType === 'single' &&
					importItemQuantity != null &&
					importItemQuantity !== 1
				) {
					return `${t('validation.incorrectValue')} ${row[quantity]}. ${t(
						'inventory.import.singleArticleQtyError',
						'Single articles can only have a quantity of 1. Remove quantity or set it as 1.',
					)}`;
				}
				if (
					!!existingArticle &&
					articleItemType === 'bulk' &&
					importItemQuantity != null &&
					quantityChange &&
					!importItemStatus
				) {
					return t(
						'inventory.import.bulkQuantityStatusError',
						'If bulk article quantity is changed, also status column needs to be defined',
					);
				}
				if (
					importItemQuantity != null &&
					importItemStatusQuantity != null &&
					importItemStatusQuantity !== importItemQuantity
				) {
					return `${t('validation.incorrectValue')} ${row[quantity]}. ${t(
						'inventory.import.quantityStatusValueMismatch',
						{
							defaultValue: 'Value does not match with the total sum of status quantities {{sum}}',
							sum: importItemStatusQuantity,
						},
					)}.`;
				}
				break;
			}
			case status: {
				const validValuesText = getValidValuesText(t, status);
				if (!!row[status] && !importItemStatus) {
					return `${t('validation.incorrectValue')} ${row[status]}. ${validValuesText}`;
				}
				if (typeof importItemStatus === 'object' && articleItemType === 'single') {
					return `${t('validation.incorrectValue')} ${row[status]}. ${validValuesText}`;
				}
				if (
					typeof importItemStatus === 'object' &&
					(Object.values(importItemStatus).some((val) => val === undefined) ||
						Object.keys(importItemStatus).some((val) => !inventoryStatusList.includes(val as any)))
				) {
					return `${t('validation.incorrectValue')} ${row[status]}. ${validValuesText}`;
				}
				if (
					statusChange &&
					importItemStatusQuantity != null &&
					importItemQuantity != null &&
					importItemStatusQuantity !== importItemQuantity
				) {
					return `${t('validation.incorrectValue')} ${row[status]}. ${t(
						'inventory.import.statusQuantityValueMismatch',
						{
							defaultValue: 'Total sum {{sum}} does not match with the quantity {{quantity}}',
							sum: importItemStatusQuantity,
							quantity: importItemQuantity,
						},
					)}.`;
				}
				if (
					!!existingArticle &&
					articleItemType === 'bulk' &&
					statusChange &&
					importItemStatusQuantity != null &&
					importItemQuantity == null
				) {
					return t(
						'inventory.import.bulkStatusQuantityError',
						'If bulk article status is changed, also quantity column needs to be defined',
					);
				}
				break;
			}
			case allocations: {
				const value = row[allocations];
				const validValuesText = getValidValuesText(t, allocations);
				if (!importItemAllocations || isEmpty(importItemAllocations)) {
					return `${t('validation.fieldCannotBeEmpty')}. ${validValuesText}`;
				}
				if (!!value && !importItemAllocations) {
					return `${t('validation.incorrectValue')} ${value}. ${validValuesText}`;
				}
				if (
					articleItemType === 'single' &&
					!isInventoryItemAllocationBoolean(importItemAllocations)
				) {
					return `${t('validation.incorrectValue')} ${value}. ${validValuesText}`;
				}
				if (
					articleItemType === 'bulk' &&
					!isInventoryItemAllocationQuantity(importItemAllocations)
				) {
					return `${t('validation.incorrectValue')} ${value}. ${t(
						'inventory.import.commaSeparatedListOfAllocations',
						'For bulk articles, please enter a comma-separated list of allocations, e.g. rental:10,sales:5',
					)}`;
				}

				if (
					articleItemType === 'bulk' &&
					isInventoryItemAllocationQuantity(importItemAllocations) &&
					typeof importItemStatus === 'object'
				) {
					const totalAllocated = sum(Object.values(importItemAllocations));
					const totalInUse = importItemStatus.IN_USE ?? 0;
					if (totalAllocated !== totalInUse) {
						return `${t('validation.incorrectValue')} ${value}. ${t(
							'inventory.import.totalSumOfAllocationsIncorrect',
							{
								defaultValue:
									'Total sum of allocations {{totalAllocated}} does not match with the amount in use {{totalInUse}}',
								totalAllocated,
								totalInUse,
							},
						)}`;
					}
				}

				break;
			}
			case type: {
				const validValuesText = getValidValuesText(t, type);
				if (!!row[type] && !importItemType) {
					return `${t('validation.incorrectValue')} ${row[type]}. ${validValuesText}`;
				}
				if (itemTypeChanged) {
					return `${t('validation.incorrectValue')} ${row[type]}. ${t(
						'inventory.import.articleTypeCannotBeChanged',
						'Type of existing article cannot be changed via import',
					)}.`;
				}
				if (!articleItemType) {
					return `${t('validation.incorrectValue')}. ${validValuesText}`;
				}
				if (!!skuItemType && !!importItemType && skuItemType !== articleItemType) {
					return `${t('validation.incorrectValue')} ${row[type]}. ${t(
						'inventory.import.articleTypeDifferentFromSku',
						'Type of article must be equal to the type of the SKU',
					)}: ${skuItemType}`;
				}
				break;
			}
			default:
				return switchUnreachable(key);
		}
		return;
	};

	if (!existingArticle) {
		const newArticleMissingFields = getNewArticleMissingRequiredFields(
			row,
			{
				isMultiStore,
				userLocations,
			},
			t,
		);
		fieldErrors = { ...fieldErrors, ...newArticleMissingFields };
	}
	getTypedKeys(getFixedArticleColumns(row)).forEach((key) => {
		const error = getInvalidFieldValue(key, row);
		if (error) {
			fieldErrors[key] = error;
		}
	});
	if (!isEmpty(fieldErrors)) {
		return { errors: fieldErrors, data: null };
	}
	return {
		errors: null,
		data: {
			articleIds: rowArticleCodes,
			skuItem: skuItem,
			locationId: itemLocationId,
			status: importItemStatus,
			allocations: importItemAllocations,
			type: importItemType,
			quantity: importItemQuantity,
			soleLength,
			dinVariation,
		},
	};
};

type ArticleImportDataResponse =
	| { type: 'equal_to_existing'; data: InventoryItem }
	| { type: 'add'; data: ArticleImport['toAdd'][number] }
	| { type: 'update'; data: ArticleImport['toUpdate'][number] }
	| { type: 'invalid'; data: ArticleImport['invalid'][number] };

interface GetSingleArticleImportData extends BaseArticleImportReq {
	row: SanitizedDataRowObject;
	existingArticle: InventoryItem | undefined;
	processedArticles: InventoryItem[];
	t: TFunction;
}

const getSingleArticleImportData = (
	args: GetSingleArticleImportData,
): ArticleImportDataResponse => {
	const {
		row,
		existingArticle,
		userLocations,
		skuItems,
		shopId,
		isMultiStore,
		existingInventoryData,
		processedArticles,
		t,
	} = args;
	const dynamicFields = getDynamicColumns(row);
	const existingArticleFieldDiff = !!existingArticle
		? getImportedArticleRowFieldDiff(row, existingArticle, userLocations)
		: undefined;
	if (!!existingArticle && !existingArticleFieldDiff?.length)
		return { type: 'equal_to_existing', data: existingArticle };
	const articleFields = validateArticleFields(
		row,
		{
			existingArticle: existingArticle,
			skuItems,
			userLocations,
			isMultiStore,
			existingInventoryData,
			processedArticles,
		},
		t,
	);
	if (!!articleFields.errors) {
		return {
			type: 'invalid',
			data: {
				csvFields: {
					data: row,
					invalidFields: articleFields.errors,
				},
			},
		};
	}
	const data = articleFields.data;
	const {
		articleIds,
		skuItem,
		locationId,
		type,
		status,
		allocations,
		quantity,
		soleLength,
		dinVariation,
	} = data;

	if (!existingArticle) {
		if (
			!skuItem ||
			!locationId ||
			!type ||
			(type !== 'single' && quantity == null) ||
			!status ||
			(type !== 'bulk' && !articleIds)
		) {
			errorHandler.report('Invalid article format', {
				skuItem,
				locationId,
				type,
				quantity,
				status,
				articleIds,
			});
			return {
				type: 'invalid',
				data: {
					csvFields: {
						data: row,
						invalidFields: {
							article_code:
								t(
									'inventory.import.invalidArticleFormatError',
									'Unknown error: Invalid article format',
								) ?? '',
						},
					},
				},
			};
		}
		const baseNewItemProps = {
			id: newFirestoreId(),
			skuCode: skuItem.skuCode,
			skuId: skuItem.id,
			shopId,
			locationId,
			identifier: articleIds ?? null,
			fieldValues: dynamicFields,
			skuName: skuItem.skuName,
			fixedFieldValues: {
				...(soleLength && { soleLength }),
				...(dinVariation && { dinVariation }),
			},
		};
		const newInventoryItem =
			type === 'single'
				? getNewInventoryItem({
						...baseNewItemProps,
						type,
						status: typeof status === 'string' ? status : 'IN_USE',
						allocation: isInventoryItemAllocationBoolean(allocations)
							? allocations
							: { rental: true },
				  })
				: getNewInventoryItem({
						...baseNewItemProps,
						type,
						quantity: quantity!,
						statuses: typeof status === 'object' ? status : { [status]: quantity },
						allocations: isInventoryItemAllocationQuantity(allocations)
							? allocations
							: { rental: quantity },
				  });
		return {
			type: 'add',
			data: {
				data: newInventoryItem,
				csvFields: {
					data: row,
				},
			},
		};
	}
	const newArticleIds = !articleIds
		? false
		: !hasSameItems(articleIds, existingArticle.identifiers);
	const baseInventoryItem: DeepPartial<BaseInventoryItem> = {
		identifiers: !!newArticleIds ? articleIds : undefined,
		skuName: skuItem?.skuName,
		skuCode: skuItem?.skuCode,
		skuId: skuItem?.id,
		locationId,
		fieldValues: !isEmpty(dynamicFields) ? dynamicFields : undefined,
		fixedFieldValues: {
			soleLength,
			dinVariation,
		},
	};
	let partialArticle: DeepPartial<SingleInventoryItem> | DeepPartial<BulkInventoryItem> = {
		...baseInventoryItem,
	};
	if (existingArticle.type === 'single') {
		const singleStatus = !!status && typeof status === 'string' ? status : undefined;
		const singleAllocations =
			!!allocations && isInventoryItemAllocationBoolean(allocations) ? allocations : undefined;
		partialArticle = {
			...partialArticle,
			status: singleStatus,
			allocation: singleAllocations,
		} as DeepPartial<SingleInventoryItem>;
	}
	if (existingArticle.type === 'bulk') {
		const bulkStatus: BulkInventoryItem['statuses'] | undefined = !status
			? undefined
			: typeof status === 'object'
			? status
			: { [status]: quantity ?? existingArticle.quantity };
		const bulkAllocations =
			!!allocations && isInventoryItemAllocationQuantity(allocations) ? allocations : undefined;
		partialArticle = {
			...partialArticle,
			quantity,
			statuses: bulkStatus,
			allocations: bulkAllocations,
		} as DeepPartial<BulkInventoryItem>;
	}
	return {
		type: 'update',
		data: {
			data: removeUndefinedValues({ ...partialArticle, id: existingArticle.id }),
			existingData: existingArticle,
			csvFields: {
				data: row,
				updatedFields: existingArticleFieldDiff ?? [],
			},
		},
	};
};

export const getDynamicColumns = (row: DataRowObject) => {
	const dynamicColumns = Object.entries(row).reduce((tot, [key, value]) => {
		if (FixedArticleColumns[key]) return tot;
		return {
			...tot,
			[key]: value,
		};
	}, {} as { [customField: string]: string });
	return dynamicColumns;
};
type FixedCols<T extends DataRowObject | SanitizedDataRowObject> = T extends SanitizedDataRowObject
	? AllowedImportField
	: FixedArticleColumnField;

const getFixedArticleColumns = <T extends DataRowObject | SanitizedDataRowObject>(row: T) => {
	const fixedColumns = getTypedKeys(row).reduce((tot, key) => {
		if (!FixedArticleColumns[key as any]) return tot;
		return {
			...tot,
			[key]: row[key],
		};
	}, {} as { [key in FixedCols<T>]?: string });
	return fixedColumns;
};
